import React, { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSortUp,
  faSortDown,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import Loading from "../../common/components/loading/loading";
import * as XLSX from "xlsx";
import { formatDate } from "../../common/services/utilities";

const AesoSummaryTable = ({
  summaryData,
  isLoading,
  pricePrecision,
  currencySymbol,
  effectiveDate,
}) => {
  const { t } = useTranslation(["marketdata"]);

  // Define cell renderers with FontAwesomeIcon
  const renderChangeCell = useCallback((params) => {
    const isPositive = params.value.changeRaw >= 0;
    return (
      <span
        style={{
          display: "inline-block",
          color: isPositive
            ? "var(--theme-green)"
            : "var(--theme-red)",
        }}
      >
        <FontAwesomeIcon
          icon={isPositive ? faSortUp : faSortDown}
          style={{
            fontSize: "1em",
            height: "1em",
            marginRight: 4,
          }}
        />{" "}
        {params.value.text}
      </span>
    );
  }, []);

  const renderColoredCell = useCallback((params) => {
    return (
      <span
        style={{
          color:
            params.value.color === "blue"
              ? "RoyalBlue"
              : params.value.color === "slate"
              ? "MediumPurple"
              : "",
        }}
      >
        {params.value.text}
      </span>
    );
  }, []);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "item",
        headerName: t("Item"),
        cellClass: "table-bold",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 90,
        resizable: true,
      },
      {
        field: "date",
        headerName: t("Date"),
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 70,
        resizable: true,
      },
      {
        field: "weightedAve7x24",
        headerName: t("WeightedAve7x24"),
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "weightedAve7x24Change",
        headerName: t("WeightedAve7x24Change"),
        type: "numericColumn",
        cellRenderer: renderChangeCell,
        cellDataType: false,
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "maxPriceHourly",
        headerName: t("MaxPriceHourly"),
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "numericAve7x24",
        headerName: t("NumericAve7x24"),
        cellRenderer: renderColoredCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "numericAve7x24Change",
        headerName: t("NumericAve7x24Change"),
        cellRenderer: renderChangeCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "peak7x16",
        headerName: t("Peak7x16"),
        cellRenderer: renderColoredCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "peak7x16Change",
        headerName: t("Peak7x16Change"),
        cellRenderer: renderChangeCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "offPeak7x8",
        headerName: t("OffPeak7x8"),
        cellRenderer: renderColoredCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "offPeak7x8Change",
        headerName: t("OffPeak7x8Change"),
        cellRenderer: renderChangeCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "abSpotNatGas",
        headerName: t("AbSpotNatGas"),
        cellRenderer: renderColoredCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
      {
        field: "weightedAverage7x24HeatRate",
        headerName: t("WeightedAverage7x24HeatRate"),
        cellRenderer: renderColoredCell,
        cellDataType: false,
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 60,
        resizable: true,
      },
    ],
    [t, renderChangeCell, renderColoredCell]
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    []
  );

  // Map to view model
  const mapToViewModel = useCallback(
    (summaryData) => {
      const dateFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "numeric",
      };

      return summaryData.map((data) => ({
        sortOrder: data.sortOrder,
        item: data.item,
        date: new Date(data.beginDateTime).toLocaleDateString(
          "en-US",
          dateFormatOptions
        ),
        weightedAve7x24: data.weightedAverage
          ? data.currencySymbol +
            data.weightedAverage.toFixed(data.pricePrecision) +
            " " +
            data.uomSymbol
          : "-",
        weightedAve7x24Change: {
          changeRaw:
            data.weightedAverage && data.weightedAverageYesterday
              ? data.weightedAverage - data.weightedAverageYesterday
              : null,
          text:
            data.weightedAverage && data.weightedAverageYesterday
              ? data.currencySymbol +
                (data.weightedAverage - data.weightedAverageYesterday).toFixed(
                  data.pricePrecision
                ) +
                " " +
                data.uomSymbol
              : "-",
        },
        maxPriceHourly: data.maxPriceHourly
          ? data.currencySymbol +
            data.maxPriceHourly.toFixed(
              data.pricePrecision + " " + data.uomSymbol
            )
          : "-",
        numericAve7x24: {
          text: data.numericAverage
            ? data.currencySymbol +
              data.numericAverage.toFixed(data.pricePrecision) +
              " " +
              data.uomSymbol
            : "-",
          color: data.uomSymbol === "MW" ? "blue" : "",
        },
        numericAve7x24Change: {
          changeRaw:
            data.numericAverage && data.numericAverageYesterday
              ? data.numericAverage - data.numericAverageYesterday
              : null,
          text:
            data.numericAverage && data.numericAverageYesterday
              ? data.currencySymbol +
                (data.numericAverage - data.numericAverageYesterday).toFixed(
                  data.pricePrecision
                ) +
                " " +
                data.uomSymbol
              : "-",
        },
        peak7x16: {
          text: data.onPeak7x16Average
            ? data.currencySymbol +
              data.onPeak7x16Average.toFixed(data.pricePrecision) +
              " " +
              data.uomSymbol
            : "-",
          color: data.uomSymbol === "MW" ? "blue" : "",
        },
        peak7x16Change: {
          changeRaw:
            data.onPeak7x16Average && data.onPeak7x16AverageYesterday
              ? data.onPeak7x16Average - data.onPeak7x16AverageYesterday
              : null,
          text:
            data.onPeak7x16Average && data.onPeak7x16AverageYesterday
              ? data.currencySymbol +
                (
                  data.onPeak7x16Average - data.onPeak7x16AverageYesterday
                ).toFixed(data.pricePrecision) +
                " " +
                data.uomSymbol
              : "-",
        },
        offPeak7x8: {
          text: data.offPeak7x8Average
            ? data.currencySymbol +
              data.offPeak7x8Average.toFixed(data.pricePrecision) +
              " " +
              data.uomSymbol
            : "-",
          color: data.uomSymbol === "MW" ? "blue" : "",
        },
        offPeak7x8Change: {
          changeRaw:
            data.offPeak7x8Average && data.offPeak7x8AverageYesterday
              ? data.offPeak7x8Average - data.offPeak7x8AverageYesterday
              : null,
          text:
            data.offPeak7x8Average && data.offPeak7x8AverageYesterday
              ? data.currencySymbol +
                (
                  data.offPeak7x8Average - data.offPeak7x8AverageYesterday
                ).toFixed(data.pricePrecision) +
                " " +
                data.uomSymbol
              : "-",
        },
        abSpotNatGas: {
          text: data.gasSettlePrice
            ? currencySymbol + data.gasSettlePrice.toFixed(pricePrecision)
            : "-",
          color: "slate",
        },
        weightedAverage7x24HeatRate: {
          text:
            data.gasSettlePrice && data.weightedAverage
              ? (data.weightedAverage / data.gasSettlePrice).toFixed(2)
              : "-",
          color: "slate",
        },
        uomSymbol: data.uomSymbol,
      }));
    },
    [currencySymbol, pricePrecision]
  );

  // Export to Excel
  const downloadToExcel = useCallback(() => {
    const excelData = summaryData.map((d) => ({
      item: d.item ?? null,
      date: formatDate(d.beginDateTime) ?? null,
      weightedAve7x24: d?.weightedAverage?.toFixed(d.pricePrecision) ?? null,
      weightedAve7x24Change:
        d?.weightedAverage && d?.weightedAverageYesterday
          ? (d?.weightedAverage - d?.weightedAverageYesterday)?.toFixed(
              d.pricePrecision
            )
          : null,
      maxPriceHourly: d?.maxPriceHourly
        ? Number(d?.maxPriceHourly)?.toFixed(d.pricePrecision)
        : null,
      numericAve7x24: d?.numericAverage?.toFixed(d.pricePrecision) ?? null,
      numericAve7x24Change:
        (d?.numericAverage - d?.numericAverageYesterday)?.toFixed(
          d.pricePrecision
        ) ?? null,
      onPeak7x16: d?.onPeak7x16Average?.toFixed(d.pricePrecision) ?? null,
      onPeak7x16Change:
        (d?.onPeak7x16Average - d?.onPeak7x16AverageYesterday)?.toFixed(
          d.pricePrecision
        ) ?? null,
      offPeak7x8: d?.offPeak7x8Average?.toFixed(d.pricePrecision) ?? null,
      offPeak7x8Change:
        (d?.offPeak7x8Average - d?.offPeak7x8AverageYesterday)?.toFixed(
          d.pricePrecision
        ) ?? null,
      abSpotNatGas: d?.gasSettlePrice?.toFixed(pricePrecision) ?? null,
      heatRate7x24: d?.gasSettlePrice
        ? (d?.weightedAverage / d?.gasSettlePrice)?.toFixed(2)
        : null,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      "AESO Summary " + formatDate(effectiveDate) + ".xlsx"
    );
  }, [summaryData, pricePrecision, effectiveDate]);

  // Create a getRowId function to ensure stable row identification
  const getRowId = useCallback(
    (params) => params.data.sortOrder.toString(),
    []
  );

  if (isLoading) return <Loading />;

  const tableData = _.orderBy(mapToViewModel(summaryData), "sortOrder", "asc");

  return (
    <div>
      <div className="row">
        <div className="col-md-10">
          <h6>AESO Summary</h6>
        </div>
        <div className="col-md-2" style={{ alignSelf: "flex-end" }}>
          <button
            style={{ float: "right" }}
            className="btn btn-sm btn-primary no-print"
            onClick={downloadToExcel}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faDownload} /> {t("Excel")}
          </button>
        </div>
      </div>
      <ThemeableAgGrid
        style={{ whiteSpace: "normal" }}
        className="table-extra-small aeso-summary-table"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={tableData}
        getRowId={getRowId}
        domLayout="autoHeight"
        animateRows={true}
        allowHorizontalScroll={false}
      />
    </div>
  );
};

export default AesoSummaryTable;
